import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Drawer,
  useMediaQuery,
  useTheme,
  Chip,
  Fab,
  Zoom,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from "@mui/material";
import {
  ZoomIn,
  ZoomOut,
  RotateLeft,
  RotateRight,
  Fullscreen,
  FullscreenExit,
  Menu,
  InsertDriveFile,
  Send,
  Download,
  Print,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";

// Mock submission data generator
const createMockSubmission = (submissionId) => ({
  id: submissionId,
  permitNumber: "PKG2024001",
  address: "123 Oak Street, Jacksonville, FL 32207",
  contractor: { name: "ABC Construction", license: "CBC012345" },
  status: "ready_for_review",
  packageTier: "Residential Standard",
  city: "Duval County",
  files: [
    {
      id: "PRF001-01-00",
      name: "Architectural Plans.pdf",
      type: "pdf",
      size: "2.5MB",
      documentType: "Architectural",
      pageCount: 8,
      uploadedAt: new Date().toISOString(),
    },
    {
      id: "PRF001-02-00",
      name: "Structural Drawings.pdf",
      type: "pdf",
      size: "1.8MB",
      documentType: "Structural",
      pageCount: 5,
      uploadedAt: new Date().toISOString(),
    },
    {
      id: "PRF001-03-00",
      name: "Energy Forms.pdf",
      type: "pdf",
      size: "1.2MB",
      documentType: "Energy",
      pageCount: 3,
      uploadedAt: new Date().toISOString(),
    },
  ],
  totalPages: 16,
});

const PpDocumentPreviewPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [submission, setSubmission] = useState(null);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  useEffect(() => {
    setSubmission(createMockSubmission(submissionId));
  }, [submissionId]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleRotate = (direction) => {
    setRotation((prev) => {
      const newRotation = direction === "left" ? prev - 90 : prev + 90;
      return newRotation >= 360 ? 0 : newRotation < 0 ? 270 : newRotation;
    });
  };

  const handleFullscreenToggle = () => {
    setFullscreen(!fullscreen);
    if (!fullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleSubmitForReview = async () => {
    try {
      // Mock submission to PP for review
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSubmitDialogOpen(false);
      navigate("/contractor/permits");
    } catch (error) {
      console.error("Failed to submit for review:", error);
    }
  };

  const activeFile = submission?.files[activeFileIndex];

  const drawerContent = (
    <Box sx={{ width: isMobile ? "100vw" : 320, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Documents ({submission?.files.length || 0})
      </Typography>

      <List>
        {submission?.files.map((file, index) => (
          <ListItem
            key={file.id}
            button
            selected={index === activeFileIndex}
            onClick={() => {
              setActiveFileIndex(index);
              if (isMobile) setDrawerOpen(false);
            }}
            sx={{
              borderRadius: 1,
              mb: 1,
              border: index === activeFileIndex ? 2 : 1,
              borderColor:
                index === activeFileIndex ? "primary.main" : "grey.300",
            }}
          >
            <ListItemIcon>
              <InsertDriveFile color="error" />
            </ListItemIcon>
            <ListItemText
              primary={file.name}
              secondary={
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {file.documentType} • {file.size}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {file.pageCount} pages
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* Document Info */}
      {activeFile && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Current Document
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {activeFile.name}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {activeFile.documentType}
            </Typography>
            <Typography variant="body2">
              <strong>Pages:</strong> {activeFile.pageCount}
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> {activeFile.size}
            </Typography>
            <Typography variant="body2">
              <strong>ID:</strong> {activeFile.id}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  if (!submission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Submission not found.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top AppBar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>

          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ mr: 2 }}>
              <Menu />
            </IconButton>
          )}

          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Document Preview - {submission.permitNumber}
          </Typography>

          {/* Zoom Controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
            <IconButton onClick={handleZoomOut} disabled={zoom <= 50}>
              <ZoomOut />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ minWidth: 60, textAlign: "center" }}
            >
              {zoom}%
            </Typography>
            <IconButton onClick={handleZoomIn} disabled={zoom >= 200}>
              <ZoomIn />
            </IconButton>
          </Box>

          {/* Rotation Controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
            <IconButton onClick={() => handleRotate("left")}>
              <RotateLeft />
            </IconButton>
            <IconButton onClick={() => handleRotate("right")}>
              <RotateRight />
            </IconButton>
          </Box>

          {/* Other Controls */}
          <IconButton onClick={handleFullscreenToggle}>
            {fullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Side Navigation Drawer */}
        {!isMobile && (
          <Drawer
            variant="persistent"
            anchor="left"
            open={drawerOpen}
            sx={{
              "& .MuiDrawer-paper": {
                position: "relative",
                borderRight: 1,
                borderColor: "divider",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Main Document Viewer */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "grey.200",
            position: "relative",
            overflow: "hidden",
            minHeight: 0, // Important for flex child to shrink
          }}
        >
          {/* Document Navigation */}
          <Box
            sx={{
              p: 1,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={() =>
                  setActiveFileIndex((prev) => Math.max(0, prev - 1))
                }
                disabled={activeFileIndex === 0}
              >
                <ArrowBack />
              </IconButton>

              <Typography variant="body2">
                Document {activeFileIndex + 1} of {submission.files.length}
              </Typography>

              <IconButton
                onClick={() =>
                  setActiveFileIndex((prev) =>
                    Math.min(submission.files.length - 1, prev + 1)
                  )
                }
                disabled={activeFileIndex === submission.files.length - 1}
              >
                <ArrowForward />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" startIcon={<Print />}>
                Print
              </Button>
              <Button size="small" startIcon={<Download />}>
                Download
              </Button>
            </Box>
          </Box>

          {/* PDF Viewer Container with Scroll */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              maxHeight: "100%",
              minHeight: 0, // Important for flex child to shrink
            }}
          >
            <Paper
              sx={{
                width: "100%",
                maxWidth: 800,
                aspectRatio: "8.5/11", // Standard document ratio
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "white",
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transition: "transform 0.3s ease",
                boxShadow: 3,
                transformOrigin: "center center",
                // Ensure the scaled element doesn't overflow its container
                maxHeight: "100%",
              }}
            >
              <Box sx={{ textAlign: "center", p: 4 }}>
                <InsertDriveFile
                  sx={{ fontSize: 80, color: "grey.400", mb: 2 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  PDF Viewer
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {activeFile?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeFile?.documentType} • Page 1 of {activeFile?.pageCount}
                </Typography>
                <Chip label={`${zoom}% zoom`} size="small" sx={{ mt: 2 }} />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Floating Action Button for Submit */}
      <Zoom in={!isMobile || drawerOpen === false}>
        <Fab
          color="primary"
          sx={{
            position: "fixed",
            bottom: 88, // Adjusted for footer height
            right: 24,
            zIndex: 1000,
          }}
          onClick={() => setSubmitDialogOpen(true)}
        >
          <Send />
        </Fab>
      </Zoom>

      {/* Submit Dialog */}
      <Dialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Send />
            Submit for Review
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Submit this document package for plan review?
          </Typography>

          <Card sx={{ mt: 2, bgcolor: "primary.light" }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Submission Summary
              </Typography>
              <Typography variant="body2">
                <strong>Permit:</strong> {submission.permitNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Address:</strong> {submission.address}
              </Typography>
              <Typography variant="body2">
                <strong>Documents:</strong> {submission.files.length} files
              </Typography>
              <Typography variant="body2">
                <strong>Total Pages:</strong> {submission.totalPages}
              </Typography>
              <Typography variant="body2">
                <strong>Package Tier:</strong> {submission.packageTier}
              </Typography>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mt: 2 }}>
            Once submitted, these documents will be assigned to a plan reviewer.
            You will be notified of the review status and any required
            corrections.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitForReview}
            startIcon={<Send />}
          >
            Submit for Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpDocumentPreviewPage;
